import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import type { ChipProps } from "@mui/material/Chip";

export const CONTRACT_STATUS_LABEL: Record<string, string> = {
  pending: "Не начался",
  active: "Действует",
  finished: "Завершён",
  canceled: "Отменён",
};

const CONTRACT_STATUS_COLOR: Record<string, ChipProps["color"]> = {
  pending: "default",
  active: "success",
  finished: "default",
  canceled: "error",
};

export const CONTRACT_ITEM_TYPE_LABEL: Record<string, string> = {
  LICENSE: "Лицензия",
  REVENUE_SHARE: "Доля с продаж",
  ONE_TIME: "Разовая работа",
};

/** Биллинг-период контракта по числу месяцев. */
export const BILLING_PERIOD_LABEL: Record<number, string> = {
  1: "Месяц",
  3: "Квартал",
  6: "Полгода",
  12: "Год",
};

export const CONTRACT_PAYMENT_PERIOD_TYPE_LABEL: Record<string, string> = {
  prepaid: "Предоплата",
  postpaid: "Постоплата",
};

export const CONTRACT_PAYMENT_STATUS_LABEL: Record<string, string> = {
  draft: "Черновик",
  pending: "К выставлению",
  wait_payment: "Ждём оплату",
  on_way: "Оплата в пути",
  paid: "Оплачен",
  paused: "Пауза",
  canceled: "Отменён",
  lost: "Списан",
};

const CONTRACT_PAYMENT_STATUS_COLOR: Record<string, ChipProps["color"]> = {
  draft: "default",
  pending: "info",
  wait_payment: "warning",
  on_way: "warning",
  paid: "success",
  paused: "default",
  canceled: "error",
  lost: "error",
};

export const REVENUE_SHARE_REPORT_STATUS_LABEL: Record<string, string> = {
  draft: "Черновик",
  payable: "К оплате",
  zero: "Нулевой",
  paid: "Оплачен",
};

const REVENUE_SHARE_REPORT_STATUS_COLOR: Record<string, ChipProps["color"]> = {
  draft: "default",
  payable: "warning",
  zero: "default",
  paid: "success",
};

export const EXCHANGE_RATE_SOURCE_LABEL: Record<string, string> = {
  ECB: "ЕЦБ",
  BNM: "Нацбанк Молдовы",
  FIXED: "фиксированный",
  FALLBACK: "ближайший доступный",
};

function chip(
  label: string,
  color: ChipProps["color"],
  key?: string
): React.ReactElement {
  return (
    <Chip key={key} label={label} color={color} size="small" variant="filled" />
  );
}

export function renderContractStatus(status: string): React.ReactNode {
  return chip(
    CONTRACT_STATUS_LABEL[status] ?? status,
    CONTRACT_STATUS_COLOR[status] ?? "default"
  );
}

/**
 * Статус платежа плюс отдельный чип просрочки: просрочка это не статус, а
 * флаг (спека §3), и её видно рядом с любым ожидающим статусом.
 */
export function renderContractPaymentStatus(
  status: string,
  expired?: boolean | null
): React.ReactNode {
  const statusChip = chip(
    CONTRACT_PAYMENT_STATUS_LABEL[status] ?? status,
    CONTRACT_PAYMENT_STATUS_COLOR[status] ?? "default",
    "status"
  );

  if (!expired) return statusChip;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {statusChip}
      {chip("Просрочен", "error", "expired")}
    </Stack>
  );
}

export function renderRevenueShareReportStatus(
  status: string
): React.ReactNode {
  return chip(
    REVENUE_SHARE_REPORT_STATUS_LABEL[status] ?? status,
    REVENUE_SHARE_REPORT_STATUS_COLOR[status] ?? "default"
  );
}
